import { type FC } from 'react';
import { Navigate, useLocation } from 'react-router';
import { buildLocalizedPath } from '@link/share/utils';

interface RedirectWithQueryProps {
  /**
   * Target path. A relative path resolves against the matched route, exactly
   * as it does on `<Navigate>`. Omit it to target the current locale root
   * (`/{lang}`). Pass a path only — the query and hash come from the entry URL.
   */
  to?: string;
}

/**
 * A canonicalizing redirect that carries the entry URL's query string, hash and
 * navigation state.
 *
 * Adding the locale segment and picking the default view correct the URL; they
 * are not navigations the visitor asked for, so they must not drop what the
 * entry URL carried. `/?mid=...`, `/?sttLang=...` and `/?utm_source=...` all
 * land on these redirects before anything has read them, and a plain
 * `<Navigate to="/ja">` resolves to a bare path with no search. The state is
 * forwarded for the same reason: a navigation to `/{lang}` that carries state
 * must reach the default view with it.
 */
const RedirectWithQuery: FC<RedirectWithQueryProps> = ({ to }) => {
  const { search, hash, state } = useLocation();
  const target = to ?? buildLocalizedPath();

  return <Navigate to={`${target}${search}${hash}`} state={state} replace />;
};

export default RedirectWithQuery;
